// Copy for the super admin Configurations page. Arabic is Egyptian, like the
// Broadcasts / Admin Accounts pages. Pick with `text[language]`.

const en = {
  lang: "en",
  title: "Company Configuration",
  subtitle: "Manage modules, permissions & splash screens",
  addCompany: "Add Company",
  loadingPage: "Loading company configuration...",
  loading: "Loading...",
  selectCompany: "Select a Company",
  selectCompanyHint: "Choose a company from the list to configure modules",
  configureModules: "Configure modules & permissions",
  rename: "Rename",
  renameTooltip: "Rename this client — cascades everywhere (employees, configurations, admin levels, etc.)",
  save: "Save",
  saving: "Saving...",
  savingShort: "Saving…",
  createAdmin: "Create Admin",
  assignAdmin: "Assign Admin",
  splashScreen: "Splash Screen",
  close: "Close",
  cancel: "Cancel",

  // company list
  companies: "Companies",
  searchCompanies: "Search companies...",
  noCompanies: "No companies found",
  active: "Active",
  inactive: "Inactive",

  // status
  companyStatus: "Company Status",
  companyActive: "Company is currently active",
  companyInactive: "Company is currently inactive",

  // subscription
  subscriptionDetails: "Subscription Details",
  startDate: "Start Date",
  endDate: "End Date",
  tier: "Subscription Tier",
  tiers: { basic: "Basic", standard: "Standard", premium: "Premium", enterprise: "Enterprise" },
  quotas: "Quotas",
  dataQuota: "Employee Data Quota",
  accountQuota: "Employee Account Quota",
  unlimited: "Unlimited",
  quotaHint:
    "Account quota is a sub-limit of the employee quota, not additional to it — e.g. 50 accounts + 150 employees means 100 more employee-only (no login) slots remain after the 50 accounts.",
  expired: "Subscription has expired",

  // splash & icon
  splashTitle: "Client Splash Screen",
  splashFor: (name) => `Displayed after login for ${name}`,
  loadingSplash: "Loading splash details...",
  noSplash: "No splash media configured",
  splashPreviewAlt: "Splash preview",
  uploadSplash: "Upload GIF or video file",
  splashUrl: "Or link to an external GIF/video",
  splashHint:
    "GIF, MP4, WEBM, MOV, or OGG files are supported (up to 25MB). Uploading a file will override the URL. Optional if an icon is set.",
  iconTitle: "Client Badge Icon",
  iconFor: (name) => `Appears as the floating company badge for ${name}`,
  iconPreviewAlt: "Icon preview",
  removeIcon: "Remove icon",
  iconWillRemove: "Icon will be removed when you save",
  noIcon: "No icon selected",
  uploadIcon: "Upload icon file",
  iconUrl: "Or link to an external icon",
  iconHint: "PNG, JPG, JPEG, SVG, WEBP, or ICO up to 5MB. Use Remove icon to clear it.",
  saveSplash: "Save Splash & Icon",
  clearSelection: "Clear Selection",
  deactivateSplash: "Deactivate Current Splash",

  // code policy
  codeFormatTitle: "Infinity Code Format",
  noPolicy: "No format configured yet — new employees get a plain numeric code.",
  policyHint: "Codes issued from here on follow this format. Already-issued codes never change.",
  locked: "Locked",
  loadingPolicy: "Loading code policy…",
  prefix: "Prefix",
  separator: "Separator",
  noneSep: "(none)",
  suffix: "Suffix (optional)",
  suffixPlaceholder: "e.g. EG",
  format: "Format",
  digits: "Digits",
  length: "Length",
  zeroPadded: "Zero-padded",
  padYes: "Yes (0001, 0002…)",
  padNo: "No (1, 2, 3…)",
  startAt: "Start at",
  casing: "Casing",
  casingOptions: { UPPER: "UPPER", LOWER: "LOWER", PRESERVE: "PRESERVE" },
  template: "Template",
  advanced: "Advanced options",
  alphabet: "Custom alphabet (optional)",
  alphabetPlaceholder: "Leave blank for the default ambiguity-safe set",
  tokens: "Available tokens",
  nextCodes: "Next codes will look like",
  generating: "Generating preview…",
  enterPrefix: "Enter a prefix to see a preview",
  enterPrefixShort: "Enter a prefix to preview",
  issued: "Issued:",
  last: "Last:",
  nextSeq: "Next seq:",
  na: "n/a",
  neverPolicy: "This client has never had a code policy.",
  discard: "Discard changes",
  saveFormat: "Save Format",
  strategies: {
    SEQUENTIAL: { label: "Sequential", hint: "Predictable, sortable, counts up" },
    RANDOM_NUMERIC: { label: "Random — numbers", hint: "Non-guessable, digits only" },
    RANDOM_ALPHANUMERIC: { label: "Random — letters & numbers", hint: "Highest entropy per character" },
    RANDOM_ALPHA: { label: "Random — letters only", hint: "Visually distinct from numeric codes" },
    TEMPLATE: { label: "Advanced (template)", hint: "Build your own, e.g. year + sequence" },
  },
  tokenDescriptions: {
    "{PREFIX}": "the client's prefix",
    "{SEP}": "the separator",
    "{SEQ:4}": "zero-padded sequence number (4 digits)",
    "{RAND:6}": "random digits (6 chars)",
    "{RANDA:6}": "random letters (6 chars)",
    "{YY}": "2-digit year",
    "{YYYY}": "4-digit year",
    "{MM}": "2-digit month",
  },

  // modules
  categoryNames: {},
  modules: {},

  // add company
  addTitle: "Add New Company",
  companyName: "Company Name",
  companyNamePlaceholder: "Enter company name...",
  codePrefix: "Infinity Code Prefix",
  codeFormat: "Code Format",
  prefixRequired: "An Infinity Code prefix is required for every client.",
  prefixInvalid: "Prefix must be 1-12 letters/numbers, no spaces or punctuation.",
  blankUnlimited: "Leave blank for unlimited",
  dataQuotaHint: "Max employees with processable data (attendance, payroll, etc).",
  accountQuotaHint:
    "Max employees who also get a login account (email/phone + password). Counts toward the employee quota above, not on top of it.",
  createCompany: "Create Company",
  creating: "Creating...",

  // rename
  renameClient: "Rename Client",
  currentName: "Current Name",
  newName: "New Name",
  newNamePlaceholder: "Enter the new client name...",
  renameWarning:
    "This immediately renames the client everywhere — every employee record, company configuration, admin level, workflow definition, code policy, splash screen, and every other table scoped to this client. This cannot be an accidental typo fix; double-check the spelling before confirming.",
  renaming: "Renaming...",

  // assign admins
  assignTitle: (name) => `Assign Admins - ${name}`,
  searchEmployees: "Search employees by name...",
  loadingEmployees: "Loading employees...",
  noEmployees: "No employees found",
  assignN: (n) => `Assign Admins (${n})`,
};

const ar = {
  lang: "ar",
  title: "إعدادات الشركات",
  subtitle: "إدارة الموديولات والصلاحيات وشاشات البداية",
  addCompany: "إضافة شركة",
  loadingPage: "جاري تحميل إعدادات الشركات...",
  loading: "جاري التحميل...",
  selectCompany: "اختار شركة",
  selectCompanyHint: "اختار شركة من القايمة عشان تظبط الموديولات بتاعتها",
  configureModules: "ظبط الموديولات والصلاحيات",
  rename: "تغيير الاسم",
  renameTooltip: "تغيير اسم العميل — بيتغير في كل حتة (الموظفين والإعدادات والمستويات الإدارية وغيرهم)",
  save: "حفظ",
  saving: "جاري الحفظ...",
  savingShort: "جاري الحفظ…",
  createAdmin: "إنشاء أدمن",
  assignAdmin: "تعيين أدمن",
  splashScreen: "شاشة البداية",
  close: "قفل",
  cancel: "إلغاء",

  companies: "الشركات",
  searchCompanies: "ابحث عن شركة...",
  noCompanies: "مفيش شركات",
  active: "شغالة",
  inactive: "متوقفة",

  companyStatus: "حالة الشركة",
  companyActive: "الشركة شغالة دلوقتي",
  companyInactive: "الشركة متوقفة دلوقتي",

  subscriptionDetails: "تفاصيل الاشتراك",
  startDate: "تاريخ البداية",
  endDate: "تاريخ النهاية",
  tier: "نوع الاشتراك",
  tiers: { basic: "أساسي", standard: "قياسي", premium: "مميز", enterprise: "مؤسسات" },
  quotas: "الحدود",
  dataQuota: "حد بيانات الموظفين",
  accountQuota: "حد حسابات الموظفين",
  unlimited: "غير محدود",
  quotaHint:
    "حد الحسابات جزء من حد الموظفين مش زيادة عليه — يعني 50 حساب + 150 موظف معناها إن فاضل 100 موظف من غير حساب دخول بعد الـ 50 حساب.",
  expired: "الاشتراك خلص",

  splashTitle: "شاشة البداية للعميل",
  splashFor: (name) => `بتظهر بعد تسجيل الدخول لـ ${name}`,
  loadingSplash: "جاري تحميل شاشة البداية...",
  noSplash: "مفيش شاشة بداية متحددة",
  splashPreviewAlt: "معاينة شاشة البداية",
  uploadSplash: "ارفع ملف GIF أو فيديو",
  splashUrl: "أو حط لينك GIF أو فيديو",
  splashHint: "ملفات GIF وMP4 وWEBM وMOV وOGG (لحد 25 ميجا). رفع ملف بيلغي اللينك. اختيارية لو فيه أيقونة.",
  iconTitle: "أيقونة شعار العميل",
  iconFor: (name) => `بتظهر كشعار الشركة المعلّق لـ ${name}`,
  iconPreviewAlt: "معاينة الأيقونة",
  removeIcon: "شيل الأيقونة",
  iconWillRemove: "الأيقونة هتتشال لما تحفظ",
  noIcon: "مفيش أيقونة",
  uploadIcon: "ارفع ملف الأيقونة",
  iconUrl: "أو حط لينك أيقونة",
  iconHint: "PNG وJPG وJPEG وSVG وWEBP وICO لحد 5 ميجا. استخدم «شيل الأيقونة» عشان تمسحها.",
  saveSplash: "احفظ شاشة البداية والأيقونة",
  clearSelection: "مسح الاختيار",
  deactivateSplash: "إيقاف شاشة البداية الحالية",

  codeFormatTitle: "شكل كود Infinity",
  noPolicy: "مفيش شكل متحدد لسه — الموظفين الجداد بياخدوا كود أرقام عادي.",
  policyHint: "الأكواد اللي هتطلع من دلوقتي هتمشي على الشكل ده، والأكواد القديمة مش هتتغير.",
  locked: "مقفول",
  loadingPolicy: "جاري تحميل شكل الكود…",
  prefix: "البادئة",
  separator: "الفاصل",
  noneSep: "(بدون)",
  suffix: "اللاحقة (اختياري)",
  suffixPlaceholder: "مثلًا EG",
  format: "الشكل",
  digits: "عدد الأرقام",
  length: "الطول",
  zeroPadded: "أصفار في الأول",
  padYes: "أيوه (0001، 0002…)",
  padNo: "لأ (1، 2، 3…)",
  startAt: "يبدأ من",
  casing: "حالة الحروف",
  casingOptions: { UPPER: "كابيتال", LOWER: "سمول", PRESERVE: "زي ما هي" },
  template: "القالب",
  advanced: "اختيارات متقدمة",
  alphabet: "حروف مخصصة (اختياري)",
  alphabetPlaceholder: "سيبها فاضية عشان تستخدم الحروف الافتراضية الواضحة",
  tokens: "الرموز المتاحة",
  nextCodes: "الأكواد الجاية هتبقى كده",
  generating: "جاري تجهيز المعاينة…",
  enterPrefix: "اكتب بادئة عشان تشوف معاينة",
  enterPrefixShort: "اكتب بادئة عشان تشوف معاينة",
  issued: "اتصرف:",
  last: "آخر كود:",
  nextSeq: "الرقم الجاي:",
  na: "مفيش",
  neverPolicy: "العميل ده ملوش شكل كود قبل كده.",
  discard: "إلغاء التعديلات",
  saveFormat: "احفظ الشكل",
  strategies: {
    SEQUENTIAL: { label: "متسلسل", hint: "متوقع ومترتب وبيزيد واحد واحد" },
    RANDOM_NUMERIC: { label: "عشوائي — أرقام", hint: "صعب يتخمّن، أرقام بس" },
    RANDOM_ALPHANUMERIC: { label: "عشوائي — حروف وأرقام", hint: "أعلى عشوائية لكل حرف" },
    RANDOM_ALPHA: { label: "عشوائي — حروف بس", hint: "شكله مختلف عن الأكواد الرقمية" },
    TEMPLATE: { label: "متقدم (قالب)", hint: "اعمله بنفسك، مثلًا السنة + رقم متسلسل" },
  },
  tokenDescriptions: {
    "{PREFIX}": "بادئة العميل",
    "{SEP}": "الفاصل",
    "{SEQ:4}": "رقم متسلسل بأصفار في الأول (4 أرقام)",
    "{RAND:6}": "أرقام عشوائية (6)",
    "{RANDA:6}": "حروف عشوائية (6)",
    "{YY}": "السنة برقمين",
    "{YYYY}": "السنة بـ 4 أرقام",
    "{MM}": "الشهر برقمين",
  },

  categoryNames: {
    Modules: "الموديولات",
    "Platform Settings": "إعدادات المنصة",
  },
  modules: {
    employeeManagement: "الموظفين",
    attendanceManagement: "الحضور والانصراف",
    leaveManagement: "إدارة الإجازات",
    payrollManagement: "المرتبات وحاسبة الضرايب",
    advancementRequests: "السلف",
    documentRequests: "طلبات المستندات",
    employeeActions: "تغييرات الموظفين",
    reportsModule: "التقارير",
    calendarModule: "التقويم والمهام",
    privateMessaging: "الرسايل (خاصة وجروبات)",
    groupsModule: "إدارة الجروبات",
    complaintsModule: "الشكاوى وتوجيه التصعيد",
    departmentsModule: "الأقسام",
    roleManagement: "المستويات الإدارية",
    systemSettings: "إعدادات الشركة",
    allowImportModeChoice: "اختيار طريقة الاستيراد (بحسابات / من غير)",
  },

  addTitle: "إضافة شركة جديدة",
  companyName: "اسم الشركة",
  companyNamePlaceholder: "اكتب اسم الشركة...",
  codePrefix: "بادئة كود Infinity",
  codeFormat: "شكل الكود",
  prefixRequired: "لازم كل عميل يبقى ليه بادئة لكود Infinity.",
  prefixInvalid: "البادئة لازم تكون من 1 لـ 12 حرف أو رقم، من غير مسافات أو علامات.",
  blankUnlimited: "سيبه فاضي لو مفيش حد",
  dataQuotaHint: "أقصى عدد موظفين ليهم بيانات بتتحسب (حضور، مرتبات، إلخ).",
  accountQuotaHint:
    "أقصى عدد موظفين ليهم حساب دخول (إيميل/تليفون + باسورد). بيتحسب من حد الموظفين اللي فوق، مش زيادة عليه.",
  createCompany: "إنشاء الشركة",
  creating: "جاري الإنشاء...",

  renameClient: "تغيير اسم العميل",
  currentName: "الاسم الحالي",
  newName: "الاسم الجديد",
  newNamePlaceholder: "اكتب اسم العميل الجديد...",
  renameWarning:
    "ده هيغير اسم العميل في كل حتة على طول — كل سجلات الموظفين وإعدادات الشركة والمستويات الإدارية وتعريفات سير العمل وشكل الكود وشاشة البداية وأي جدول تاني تبع العميل ده. ده مش مكان لتصليح غلطة كتابة بالصدفة؛ راجع الاسم كويس قبل ما تأكد.",
  renaming: "جاري تغيير الاسم...",

  assignTitle: (name) => `تعيين أدمن - ${name}`,
  searchEmployees: "ابحث عن موظف بالاسم...",
  loadingEmployees: "جاري تحميل الموظفين...",
  noEmployees: "مفيش موظفين",
  assignN: (n) => `تعيين الأدمن (${n})`,
};

export const text = { en, ar };

// The hooks report results in English (they are shared and stay untouched);
// this turns each known message into Arabic when the page is in Arabic.
// Server-supplied messages pass through unchanged.
const MESSAGES_AR = {
  "Please select a company first": "اختار شركة الأول",
  "Select a company first": "اختار شركة الأول",
  "Failed to load employees": "معرفناش نحمّل الموظفين",
  "Please select at least one employee": "اختار موظف واحد على الأقل",
  "Failed to assign admins": "معرفناش نعيّن الأدمن",
  "Admins assigned successfully!": "الأدمن اتعيّنوا بنجاح!",
  "Failed to load code policy": "معرفناش نحمّل شكل الكود",
  "Could not preview this format": "معرفناش نعمل معاينة للشكل ده",
  "Failed to save code policy": "معرفناش نحفظ شكل الكود",
  "Code policy saved successfully!": "شكل الكود اتحفظ بنجاح!",
  "Failed to load companies": "معرفناش نحمّل الشركات",
  "Please enter a company name": "اكتب اسم الشركة",
  "Failed to create company": "معرفناش ننشئ الشركة",
  "Company created successfully!": "الشركة اتعملت بنجاح!",
  "Please enter a new client name": "اكتب اسم العميل الجديد",
  "New client name must be different from the current one": "الاسم الجديد لازم يبقى مختلف عن الحالي",
  "Failed to rename client": "معرفناش نغيّر اسم العميل",
  "Failed to load permissions": "معرفناش نحمّل الصلاحيات",
  "Failed to save configuration": "معرفناش نحفظ الإعدادات",
  "Configuration saved successfully!": "الإعدادات اتحفظت بنجاح!",
  "Failed to load splash screen": "معرفناش نحمّل شاشة البداية",
  "Only GIF, MP4, WEBM, MOV, or OGG files are allowed": "مسموح بس بملفات GIF وMP4 وWEBM وMOV وOGG",
  "Icon must be PNG, JPG, JPEG, SVG, WEBP, or ICO": "الأيقونة لازم تكون PNG أو JPG أو JPEG أو SVG أو WEBP أو ICO",
  "Icon must be 5MB or smaller": "الأيقونة لازم تكون 5 ميجا أو أقل",
  "Add splash media or an icon": "ضيف شاشة بداية أو أيقونة",
  "Failed to save splash screen": "معرفناش نحفظ شاشة البداية",
  "Splash screen saved successfully": "شاشة البداية اتحفظت بنجاح",
  "Failed to deactivate splash screen": "معرفناش نوقف شاشة البداية",
  "Splash screen deactivated": "شاشة البداية اتوقفت",
};

export const translateMessage = (message, language) => {
  if (language !== "ar" || !message) return message;
  if (MESSAGES_AR[message]) return MESSAGES_AR[message];
  const renamed = /^Client renamed to "(.*)" everywhere/.exec(message);
  if (renamed) {
    return `اسم العميل اتغيّر لـ "${renamed[1]}" في كل حتة — الموظفين والإعدادات والمستويات الإدارية وسير العمل وغيرهم.`;
  }
  return message;
};

export default text;
